// === Root App Component ===

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AuthProvider } from './components/AuthProvider';
import { ProfileProvider } from './lib/profile';
import { CardAccessProvider } from './lib/card-access-context';
import { WebThemeProvider } from './lib/theme-context';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MfaChallengeGate } from './components/MfaChallengeGate';
import { AppLayout } from './components/AppLayout';

const DashboardPage = lazy(() => import('./pages/dashboard').then((module) => ({ default: module.DashboardPage })));
const LoginPage = lazy(() => import('./pages/login').then((module) => ({ default: module.LoginPage })));
const AuthHandoffPage = lazy(() => import('./pages/auth-handoff').then((module) => ({ default: module.AuthHandoffPage })));
const SelectCompanyPage = lazy(() => import('./pages/select-company').then((module) => ({ default: module.SelectCompanyPage })));
const BlocksListPage = lazy(() => import('./pages/blocks/blocks-list').then((module) => ({ default: module.BlocksListPage })));
const GeneralTasksListPage = lazy(() => import('./pages/general-tasks/general-tasks-list').then((module) => ({ default: module.GeneralTasksListPage })));
const GeneralTaskFormPage = lazy(() => import('./pages/general-tasks/general-task-form').then((module) => ({ default: module.GeneralTaskFormPage })));
const PreGroundWorksListPage = lazy(() => import('./pages/pre-ground-works/pre-ground-works-list').then((module) => ({ default: module.PreGroundWorksListPage })));
const ChemicalTemplatesListPage = lazy(() => import('./pages/chemical-templates/chemical-templates-list').then((module) => ({ default: module.ChemicalTemplatesListPage })));
const PlantingTemplatesListPage = lazy(() => import('./pages/planting-templates/planting-templates-list').then((module) => ({ default: module.PlantingTemplatesListPage })));
const FertilizerTemplatesListPage = lazy(() => import('./pages/fertilizer-templates/fertilizer-templates-list').then((module) => ({ default: module.FertilizerTemplatesListPage })));
const PestDiseaseObservationsListPage = lazy(() => import('./pages/pest-disease-observations/pest-disease-observations-list').then((module) => ({ default: module.PestDiseaseObservationsListPage })));
const SprayTaskListPage = lazy(() => import('./pages/spray-task/spray-task-list').then((module) => ({ default: module.SprayTaskListPage })));
const IrrigationTaskListPage = lazy(() => import('./pages/irrigation-task/irrigation-task-list').then((module) => ({ default: module.IrrigationTaskListPage })));
const HarvestTemplatesListPage = lazy(() => import('./pages/harvest-templates/harvest-templates-list').then((module) => ({ default: module.HarvestTemplatesListPage })));
const DailyCheckSheetsListPage = lazy(() => import('./pages/daily-check-sheets/daily-check-sheets-list').then((module) => ({ default: module.DailyCheckSheetsListPage })));
const TimesheetListPage = lazy(() => import('./pages/timesheet/timesheet-list').then((module) => ({ default: module.TimesheetListPage })));
const StaffManagementPage = lazy(() => import('./pages/admin/staff-management').then((module) => ({ default: module.StaffManagementPage })));
const UserTitlesPage = lazy(() => import('./pages/titles/user-titles').then((module) => ({ default: module.UserTitlesPage })));
const CompanySettingsPage = lazy(() => import('./pages/admin/company-settings').then((module) => ({ default: module.CompanySettingsPage })));
const ProductSettingsPage = lazy(() => import('./pages/admin/product-settings').then((module) => ({ default: module.ProductSettingsPage })));
const PackCountListPage = lazy(() => import('./pages/settings/pack-types-list').then((module) => ({ default: module.PackCountListPage })));
const PackCategoriesListPage = lazy(() => import('./pages/settings/pack-categories-list').then((module) => ({ default: module.PackCategoriesListPage })));
const SwmsRankingsListPage = lazy(() => import('./pages/settings/swms-rankings-list').then((module) => ({ default: module.SwmsRankingsListPage })));
const SwmsTasksListPage = lazy(() => import('./pages/settings/swms-tasks-list').then((module) => ({ default: module.SwmsTasksListPage })));
const PreHarvestChecksListPage = lazy(() => import('./pages/settings/harvest-checks-list').then((module) => ({ default: module.PreHarvestChecksListPage })));
const DailyCheckListSettingsPage = lazy(() => import('./pages/settings/checklist-list').then((module) => ({ default: module.DailyCheckListSettingsPage })));
const PreCheckSettingsPage = lazy(() => import('./pages/settings/checklist-list').then((module) => ({ default: module.PreCheckSettingsPage })));
const PostCheckSettingsPage = lazy(() => import('./pages/settings/checklist-list').then((module) => ({ default: module.PostCheckSettingsPage })));
const PostHarvestChecksListPage = lazy(() => import('./pages/settings/harvest-checks-list').then((module) => ({ default: module.PostHarvestChecksListPage })));
const KpaPsiListPage = lazy(() => import('./pages/settings/kpa-psi-list').then((module) => ({ default: module.KpaPsiListPage })));
const SprayTanksListPage = lazy(() => import('./pages/settings/spray-tanks-list').then((module) => ({ default: module.SprayTanksListPage })));
const MlHoursListPage = lazy(() => import('./pages/settings/ml-hours-list').then((module) => ({ default: module.MlHoursListPage })));
const ShedsListPage = lazy(() => import('./pages/settings/sheds-list').then((module) => ({ default: module.ShedsListPage })));
const ContractorsListPage = lazy(() => import('./pages/settings/contractors-list').then((module) => ({ default: module.ContractorsListPage })));
const TaskTypesListPage = lazy(() => import('./pages/settings/task-types-list').then((module) => ({ default: module.TaskTypesListPage })));
const ChemicalsListPage = lazy(() => import('./pages/settings/chemicals-list').then((module) => ({ default: module.ChemicalsListPage })));
const FertilizersListPage = lazy(() => import('./pages/settings/fertilizers-list').then((module) => ({ default: module.FertilizersListPage })));
const StockInventoryPage = lazy(() => import('./pages/stock-inventory/stock-inventory').then((module) => ({ default: module.default })));
const PrivacyViewPage = lazy(() => import('./pages/settings/privacy-view').then((module) => ({ default: module.PrivacyViewPage })));
const TermsViewPage = lazy(() => import('./pages/settings/privacy-view').then((module) => ({ default: module.TermsViewPage })));
const BugReportPage = lazy(() => import('./pages/settings/bug-report').then((module) => ({ default: module.BugReportPage })));
const ReportsHubPage = lazy(() => import('./pages/reports/reports-hub').then((module) => ({ default: module.ReportsHubPage })));
const TodoListPage = lazy(() => import('./pages/todo/todo-list').then((module) => ({ default: module.TodoListPage })));

function RouteLoadingFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <CircularProgress size={28} />
    </Box>
  );
}
// WebThemeProvider creates its own MuiThemeProvider internally,
// so we no longer need a separate ThemeProvider wrapper here.
export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
        <CardAccessProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <WebThemeProvider>
          <CssBaseline />
          <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/handoff" element={<AuthHandoffPage />} />
            {/* Superadmin company picker. Not wrapped in ProtectedRoute — that
                would bounce back here in a loop, since a superadmin reaches it
                precisely because they have no company yet. It does its own
                auth/role checks. */}
            <Route path="/select-company" element={
              // Gated for MFA but NOT by ProtectedRoute: a superadmin reaches
              // this page precisely because they have no company yet, so
              // ProtectedRoute would bounce them back here in a loop. Without
              // the gate, an unsatisfied second factor would 403 every call this
              // page makes and apiFetch would sign them out.
              <MfaChallengeGate><SelectCompanyPage /></MfaChallengeGate>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AppLayout><DashboardPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/blocks" element={
              <ProtectedRoute>
                <AppLayout><BlocksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            {/* Legacy standalone add/edit page removed — the maintained flow is
                the Add/Edit dialog on the blocks list. Redirect old URLs there
                so a single code path (list + dialog + API) is the only way to
                create/edit blocks. */}
            <Route path="/blocks/add" element={<Navigate to="/blocks" replace />} />
            <Route path="/blocks/:id/edit" element={<Navigate to="/blocks" replace />} />
            <Route path="/general-tasks" element={
              <ProtectedRoute>
                <AppLayout><GeneralTasksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/general-tasks/add" element={
              <ProtectedRoute>
                <AppLayout><GeneralTaskFormPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/general-tasks/:id/edit" element={
              <ProtectedRoute>
                <AppLayout><GeneralTaskFormPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/pre-ground-works" element={
              <ProtectedRoute>
                <AppLayout><PreGroundWorksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/chemical-templates" element={
              <ProtectedRoute>
                <AppLayout><ChemicalTemplatesListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/planting-templates" element={
              <ProtectedRoute>
                <AppLayout><PlantingTemplatesListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/fertilizer-templates" element={
              <ProtectedRoute>
                <AppLayout><FertilizerTemplatesListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/pest-disease-observations" element={
              <ProtectedRoute>
                <AppLayout><PestDiseaseObservationsListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/spray-task" element={
              <ProtectedRoute>
                <AppLayout><SprayTaskListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/irrigation-task" element={
              <ProtectedRoute>
                <AppLayout><IrrigationTaskListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/harvest-templates" element={
              <ProtectedRoute>
                <AppLayout><HarvestTemplatesListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/daily-check-sheets" element={
              <ProtectedRoute>
                <AppLayout><DailyCheckSheetsListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/timesheet" element={
              <ProtectedRoute>
                <AppLayout><TimesheetListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/staff" element={
              <ProtectedRoute>
                <AppLayout><StaffManagementPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/titles" element={
              <ProtectedRoute>
                <AppLayout><UserTitlesPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <AppLayout><CompanySettingsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={<Navigate to="/settings" replace />} />
            <Route path="/program-settings" element={
              <ProtectedRoute>
                <AppLayout><ProductSettingsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/pack-count" element={
              <ProtectedRoute>
                <AppLayout><PackCountListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/pack-categories" element={
              <ProtectedRoute>
                <AppLayout><PackCategoriesListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/swms-rankings" element={
              <ProtectedRoute>
                <AppLayout><SwmsRankingsListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/swms-tasks" element={
              <ProtectedRoute>
                <AppLayout><SwmsTasksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/pre-harvest-checks" element={
              <ProtectedRoute>
                <AppLayout><PreHarvestChecksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/daily-check-list" element={
              <ProtectedRoute>
                <AppLayout><DailyCheckListSettingsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/pre-check" element={
              <ProtectedRoute>
                <AppLayout><PreCheckSettingsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/post-check" element={
              <ProtectedRoute>
                <AppLayout><PostCheckSettingsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/post-harvest-settings" element={
              <ProtectedRoute>
                <AppLayout><PostHarvestChecksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/kpa-psi" element={
              <ProtectedRoute>
                <AppLayout><KpaPsiListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/spray-tanks" element={
              <ProtectedRoute>
                <AppLayout><SprayTanksListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/ml-hours" element={
              <ProtectedRoute>
                <AppLayout><MlHoursListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/sheds" element={
              <ProtectedRoute>
                <AppLayout><ShedsListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/contractors" element={
              <ProtectedRoute>
                <AppLayout><ContractorsListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/task-types" element={
              <ProtectedRoute>
                <AppLayout><TaskTypesListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/chemicals" element={
              <ProtectedRoute>
                <AppLayout><ChemicalsListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/fertilizers" element={
              <ProtectedRoute>
                <AppLayout><FertilizersListPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/stock-inventory" element={
              <ProtectedRoute>
                <AppLayout><StockInventoryPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/privacy" element={
              <ProtectedRoute>
                <AppLayout><PrivacyViewPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/terms" element={
              <ProtectedRoute>
                <AppLayout><TermsViewPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/bug-report" element={
              <ProtectedRoute>
                <AppLayout><BugReportPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <AppLayout><ReportsHubPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/todo" element={
              <ProtectedRoute>
                <AppLayout><TodoListPage /></AppLayout>
              </ProtectedRoute>
            } />
          </Routes>
          </Suspense>
        </WebThemeProvider>
        </LocalizationProvider>
        </CardAccessProvider>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
